import {CopyOutlined, DeleteFilled, MinusSquareOutlined, PlusOutlined, PlusSquareOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, message, Modal, Space, Spin, Table} from "antd";
import {ColumnsType} from "antd/es/table";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import _ from "lodash";
import {AppContextContext, ItemUpdateProfileServiceContext, VulSourceServiceContext} from "../../Contexts";
import {Permission} from "../../domain/auth/Permission";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import ItemUpdateProfile from "../../domain/ItemUpdateProfile";
import VulSource from "../../domain/VulSource";
import QueryOptions from "../../sal-ui/QueryOptions";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import * as globalStyles from "../App.module.css";
import {DocumentTitle} from "../DocumentTitle";
import MoreLess from "../common/MoreLess";
import {renderOrganizationLink} from "../common/OrganizationLink";
import Tip from "../common/Tip";
import ReadOnlyQueryView from "../vulview/ReadOnlyQueryView";
import * as styles from "./ItemUpdateProfileList.module.css";
import {FormPersister} from "../../sal-ui/FormPersister";
import OwnerFilter, {createDefaultOwnerFilterValues, createOwnerRqbQuery, loadPersistentOwnerFilterValues} from "../common/OwnerFilter";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const PERSISTENT_IDENT = "ItemUpdateProfileList";
const FILTER_DEBOUNCE_MS = 500;

function ItemUpdateProfileList() {
    const appContext = useContext(AppContextContext);
    const service = useContext(ItemUpdateProfileServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState<PagedResult<ItemUpdateProfile>>();
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [sources, setSources] = useState<VulSource[]>();
    const [filterValues, setFilterValues] = useState<any>();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);
    const hasManage = appContext.user.hasPermission(Permission.ITEM_UPDATE_PROFILE__MANAGE_ORG);
    const ownerFilterAccess = {
        canReadSystem: appContext.user.hasPermission(Permission.ITEM_UPDATE_PROFILE__READ_SEAL)
                       || appContext.user.hasPermission(Permission.ITEM_UPDATE_PROFILE__MANAGE_ORG),
        canReadOrganization: appContext.user.hasPermission(Permission.ITEM_UPDATE_PROFILE__READ_ORG)
                             || appContext.user.hasPermission(Permission.ITEM_UPDATE_PROFILE__MANAGE_ORG)
    };
    const title = "Item update profiles";

    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setSources(value.data));

        const values = loadPersistentOwnerFilterValues({
            appContext,
            access: ownerFilterAccess,
            filterFormPersister,
            location,
            navigate
        });

        setFilterValues(values);
    }, []);

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb
                    className={globalStyles["common__breadcrumb"]}
                    items={[
                        {title: appContext.config?.appName},
                        {title}
                    ]}
                />

                <h1>{title}</h1>

                <Tip
                    id="ItemUpdateProfileList.purpose"
                    style={{marginBottom: 12}}
                    message="Item update profiles define which attribute changes matter."
                    description="They are used by reports and AI extractors to ignore routine source updates and process only vulnerability items whose attribute changes match the defined rules."
                />

                <OwnerFilter
                    className={styles.filter}
                    values={filterValues}
                    defaultValues={createDefaultOwnerFilterValues(appContext, ownerFilterAccess)}
                    persistentStateIdent={PERSISTENT_IDENT}
                    canReadSystem={ownerFilterAccess.canReadSystem}
                    canReadOrganization={ownerFilterAccess.canReadOrganization}
                    onChange={setFilterValues}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    {hasManage &&
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={() => navigate("/definitions/item-update-profiles/add")}
                        >
                            Add
                        </Button>
                    }

                    <Button className="btn-seamless" icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>

                    <Button
                        className="btn-seamless"
                        icon={<PlusSquareOutlined/>}
                        title="Expand all"
                        onClick={() => setExpandedRowKeys(profiles?.data?.map(value => value.id) ?? [])}
                    />
                    <Button
                        className="btn-seamless"
                        icon={<MinusSquareOutlined/>}
                        title="Collapse all"
                        onClick={() => setExpandedRowKeys([])}
                    />
                </div>

                <Table
                    className={styles.table}
                    dataSource={profiles?.data}
                    loading={tableHandler.loading}
                    pagination={tableHandler.pagination}
                    showSorterTooltip={false}
                    rowKey="id"
                    onChange={tableHandler.onTableChange}
                    columns={getColumns()}
                    expandable={{
                        expandedRowKeys: expandedRowKeys,
                        onExpand: (expanded, record) => {
                            if (expanded) {
                                setExpandedRowKeys(prevState => [...prevState, record.id]);
                            } else {
                                setExpandedRowKeys(prevState => prevState.filter(value => value !== record.id));
                            }
                        },
                        expandedRowRender: record => (
                            <div className={styles.query}>
                                <h2>Rules</h2>

                                {sources
                                    ? <ReadOnlyQueryView sources={sources} query={JSON.parse(record.query)}/>
                                    : <Spin/>}
                            </div>
                        )
                    }}
                />
            </>
        </DocumentTitle>
    );

    function getColumns(): ColumnsType<ItemUpdateProfile> {
        const columns: ColumnsType<ItemUpdateProfile> = [
            {
                dataIndex: "name",
                title: "Name",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                render: renderName
            },
            {
                dataIndex: ["organization", "name"],
                key: "organization.name",
                title: "Owner",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                align: "left",
                render: renderOrganization
            },
            {
                dataIndex: "description",
                title: "Description",
                render: value => value ? <MoreLess>{value}</MoreLess> : <i>no description</i>
            }
        ];

        if (hasManage) {
            columns.push({
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            });
        }

        return columns;
    }

    function renderName(value: unknown, profile: ItemUpdateProfile) {
        return <Link to={`/definitions/item-update-profiles/${profile.id}`}>{String(value)}</Link>;
    }

    function renderOrganization(value: unknown, profile: ItemUpdateProfile) {
        if (profile.owner === ResourceOwnerType.SYSTEM) {
            return <i>System</i>;
        }

        return renderOrganizationLink(profile.organization, value);
    }

    function renderAction(_: unknown, profile: ItemUpdateProfile) {
        return (
            <Space>
                <Button icon={<CopyOutlined/>}
                        className="ant-btn-icon-only"
                        title="Clone"
                        onClick={() => navigate(`/definitions/item-update-profiles/clone/${profile.id}`)}/>

                {(profile.owner !== ResourceOwnerType.SYSTEM || appContext.user.isSystemAdmin()) &&
                    <Button
                        icon={<DeleteFilled/>}
                        className="ant-btn-icon-only"
                        title="Delete"
                        onClick={() => {
                            Modal.confirm({
                                content: <>Do you really want to delete item update profile <b>{profile.name}</b>?</>,
                                okText: "Delete",
                                cancelText: "Cancel",
                                onOk: () => onDeleteConfirm(profile)
                            });
                        }}
                    />}
            </Space>
        );
    }

    function onDeleteConfirm(profile: ItemUpdateProfile) {
        service.delete(profile)
            .then(() => {
                message.success("Item update profile deleted.");

                tableHandler.reload();
            });
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createOwnerRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function reload() {
        return service.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);

                setProfiles(result);
            });
    }
}

export default ItemUpdateProfileList;
